import { createContext, useContext, useEffect, useState, useMemo, type ReactNode } from "react";
import { getSession, setSession, isStaffRole, type PermissionMatrix, type Session } from "../components/feature/auth/session";
import { authService } from "../services/apiServices";

interface PermissionContextType {
  role: string;
  isSuperAdmin: boolean;
  isStaff: boolean;
  permissions: PermissionMatrix;
  can: (module: string, action: "view" | "create" | "edit" | "approve" | "delete") => boolean;
  canView: (module: string) => boolean;
  canCreate: (module: string) => boolean;
  canEdit: (module: string) => boolean;
  canApprove: (module: string) => boolean;
  canDelete: (module: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<Session | null>(getSession);

  const refreshPermissions = async () => {
    try {
      const s = getSession();
      if (!s || !s.token) return;
      const res = await authService.getMe();
      if (res && res.data && res.data.user) {
        const u = res.data.user;
        const updated: Session = {
          ...s,
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          isStaff: u.isStaff,
          permissions: u.permissions || s.permissions,
          customPermissions: u.customPermissions || s.customPermissions,
        };
        setSession(updated);
        setSessionState(updated);
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const custom = e as CustomEvent<Session | null>;
      setSessionState(custom.detail ?? getSession());
    };

    window.addEventListener("mandatepro_session_updated", handleUpdate);
    return () => window.removeEventListener("mandatepro_session_updated", handleUpdate);
  }, []);

  const role = session?.role || "retailer";
  const normalizedRole = role.toLowerCase().replace(/[\s-]+/g, "_");
  const isSuperAdmin = normalizedRole === "super_admin";
  const isStaff = isStaffRole(normalizedRole) || role === "admin";
  const permissions: PermissionMatrix = useMemo(() => session?.permissions || {}, [session?.permissions]);

  const can = (module: string, action: "view" | "create" | "edit" | "approve" | "delete"): boolean => {
    if (isSuperAdmin) return true;
    const mod = permissions[module];
    if (!mod) return false;
    return Boolean(mod[action]);
  };

  const canView = (module: string) => can(module, "view");
  const canCreate = (module: string) => can(module, "create");
  const canEdit = (module: string) => can(module, "edit");
  const canApprove = (module: string) => can(module, "approve");
  const canDelete = (module: string) => can(module, "delete");

  const value: PermissionContextType = {
    role: normalizedRole,
    isSuperAdmin,
    isStaff,
    permissions,
    can,
    canView,
    canCreate,
    canEdit,
    canApprove,
    canDelete,
    refreshPermissions,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermission(): PermissionContextType {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    // Graceful fallback if outside provider
    const s = getSession();
    const role = (s?.role || "retailer").toLowerCase().replace(/[\s-]+/g, "_");
    const isSuperAdmin = role === "super_admin";
    const perms = s?.permissions || {};
    return {
      role,
      isSuperAdmin,
      isStaff: isStaffRole(role) || s?.role === "admin",
      permissions: perms,
      can: (mod, act) => isSuperAdmin || Boolean(perms[mod]?.[act]),
      canView: (mod) => isSuperAdmin || Boolean(perms[mod]?.view),
      canCreate: (mod) => isSuperAdmin || Boolean(perms[mod]?.create),
      canEdit: (mod) => isSuperAdmin || Boolean(perms[mod]?.edit),
      canApprove: (mod) => isSuperAdmin || Boolean(perms[mod]?.approve),
      canDelete: (mod) => isSuperAdmin || Boolean(perms[mod]?.delete),
      refreshPermissions: async () => {},
    };
  }
  return ctx;
}
